import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { environment } from '../config/environment';

const PlanSelector = ({ user }: { user: any }) => {
  const isProUser = user['custom:plan'] === 'pro';
  const [billingPeriod, setBillingPeriod] = React.useState<'yearly' | 'monthly'>('yearly');

  const priceIds = {
    yearly: environment.stripe.priceYearly,
    monthly: environment.stripe.priceMonthly,
  };

  const pricing = {
    yearly: { amount: 35, period: 'year', save: 'Save $13', perMonth: 2.92, savePercent: '27%' },
    monthly: { amount: 4, period: 'month', save: null, perMonth: null, savePercent: null },
  };

  const selectedPrice = pricing[billingPeriod];

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Free Plan */}
      <div className={`plan-card plan-card-free ${!isProUser ? 'plan-card-active' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'}`}>
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-gray-800 mb-3">Free</h3>
          <div className="text-5xl font-bold text-gray-900 mb-3">$0</div>
          <p className="text-gray-600 text-lg">Perfect for getting started</p>
        </div>

        {!isProUser && (
          <div className="mb-6 text-center">
            <span className="badge-primary px-5">CURRENT PLAN</span>
          </div>
        )}

        <div className="feature-list mb-6">
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-700 text-base"><strong>5 saved quizzes</strong> max</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-700 text-base"><strong>10 questions</strong> per quiz</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-700 text-base"><strong>10 participants</strong> per game</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-700 text-base">AI generation (GPT-4o-mini)</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-warn">⏰</span>
            <span className="text-gray-700 text-base">Game starts in ~30 seconds</span>
          </div>
        </div>
      </div>

      {/* Pro Plan */}
      <div className={`plan-card plan-card-pro relative ${isProUser ? 'plan-card-active' : 'hover:border-blue-400 hover:shadow-xl'}`}>
        <div className="absolute -top-4 right-6">
          <div className="badge-gradient px-4">BEST VALUE</div>
        </div>

        <div className="text-center mb-6 mt-4">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">Pro</h3>

          {/* Billing Period Toggle */}
          {!isProUser && (
            <div className="mb-6">
              <div className="text-sm font-medium text-gray-600 mb-2 text-center">Select billing period:</div>
              <div className="inline-flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-1.5 border-2 border-blue-200 shadow-md">
                <button
                  type="button"
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-6 py-3 rounded-lg text-base font-bold transition-all duration-200 ${
                    billingPeriod === 'monthly'
                      ? 'bg-white text-gray-900 shadow-lg scale-105'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingPeriod('yearly')}
                  className={`relative px-6 py-3 rounded-lg text-base font-bold transition-all duration-200 ${
                    billingPeriod === 'yearly'
                      ? 'bg-white text-gray-900 shadow-lg scale-105'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  Yearly
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md">
                    Save 27%
                  </span>
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-5xl font-bold gradient-text-blue">
              ${selectedPrice.amount}<span className="text-2xl text-gray-600">/{selectedPrice.period}</span>
            </div>
            {selectedPrice.perMonth && (
              <div className="text-lg text-gray-500">
                Only <span className="font-semibold text-gray-700">${selectedPrice.perMonth}/month</span> billed annually
              </div>
            )}
            {selectedPrice.save && (
              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-base font-bold text-green-700">{selectedPrice.save} compared to monthly</span>
              </div>
            )}
          </div>
          <p className="text-gray-600 text-lg mt-4">Full commitment, maximum value</p>
        </div>

        {isProUser && (
          <div className="mb-6 text-center">
            <span className="badge-secondary px-5">CURRENT PLAN</span>
          </div>
        )}

        <div className="feature-list mb-8">
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-800 font-semibold text-base"><strong>Unlimited saved quizzes</strong></span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-800 font-semibold text-base"><strong>100 participants</strong> per game 🔥</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-800 font-semibold text-base"><strong>20 questions</strong> per quiz</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-800 font-semibold text-base">Advanced AI (GPT-4o) + verified accuracy ✨</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon-check">✓</span>
            <span className="text-gray-800 font-semibold text-base">Fast game start (10-15 seconds) ⚡</span>
          </div>
        </div>

        {!isProUser ? (
          <form action={`${environment.api.stripe}/create-checkout-session`} method="POST">
            <input type="hidden" name="email" value={user?.email || ''} />
            <input type="hidden" name="priceId" value={priceIds[billingPeriod]} />
            <button type="submit" className="btn-primary w-full py-4 text-lg font-bold hover:scale-105 transition-transform duration-200">
              {billingPeriod === 'yearly' ? (
                <>Upgrade to Pro - ${selectedPrice.amount}/{selectedPrice.period} 🎉</>
              ) : (
                <>Upgrade to Pro - ${selectedPrice.amount}/{selectedPrice.period}</>
              )}
            </button>
          </form>
        ) : (
          <form action={`${environment.api.stripe}/create-portal-session`} method="POST">
            <input type="hidden" name="email" value={user?.email || ''} />
            <button type="submit" className="btn-secondary w-full py-4">
              Manage Subscription
            </button>
          </form>
        )}
      </div>
    </div>
  );
};


const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUserFromToken } = useAuth();

  // Refresh user data on mount to get latest subscription info from Cognito
  React.useEffect(() => {
    const refreshData = async () => {
      await refreshUserFromToken();
    };
    if (isAuthenticated) {
      refreshData();
    }
  }, [isAuthenticated, refreshUserFromToken]);

  if (!isAuthenticated || !user) {
    navigate('/');
    return null;
  }

  const displayName = user?.name || user?.given_name || 'User';
  const initial = (displayName[0] || "?").toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 antialiased">
      <Header />

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/')} className="btn-ghost group">
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500 rounded-full shadow-md">
              <span className="text-2xl font-bold text-white">{initial}</span>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Profile</h1>
              <p className="text-base text-gray-500 mt-1">Manage your account and subscription</p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Information</h2>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Full Name</dt>
                  <dd className="text-lg font-semibold text-gray-900">{user.name || 'Not provided'}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Email Address</dt>
                  <dd className="text-lg font-semibold text-gray-900 break-all">{user.email || 'Not provided'}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">First Name</dt>
                  <dd className="text-lg font-semibold text-gray-900">{user.given_name || 'Not provided'}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Last Name</dt>
                  <dd className="text-lg font-semibold text-gray-900">{user.family_name || 'Not provided'}</dd>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Plans</h2>

            {user['custom:sub_end_date'] && (
              <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      <strong>Subscription Ending:</strong> Your Pro plan will end on <strong>{new Date(user['custom:sub_end_date']).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                      You can reactivate your subscription anytime using the "Manage Subscription" button below.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <PlanSelector user={user} />
          </section>
        </div>
      </main>
    </div>
  );
};

export default Profile;